import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  Plus,
  Trash2,
  User,
  Shield,
  Crown,
  Pencil,
  ChevronUp,
  ChevronDown,
  UserPlus,
} from 'lucide-react';
import { useProjects } from '@/hooks/useProjects';
import { useProjectMutations } from '@/hooks/useProjectMutations';
import {
  useProjectColumns,
  useColumnMutations,
} from '@/hooks/useColumnMutations';
import { RepoPickerDialog } from '@/components/dialogs/shared/RepoPickerDialog';
import {
  CreateColumnDialog,
  EditColumnDialog,
  DeleteColumnDialog,
} from '@/components/dialogs';
import {
  projectsApi,
  projectMembersApi,
  usersApi,
} from '@/lib/api';
import { repoBranchKeys } from '@/hooks/useRepoBranches';
import { usePermissions } from '@/hooks/usePermissions';
import type { Project, Repo, UpdateProject } from 'shared/types';

import { COLUMN_COLORS_MAP } from '@/constants/columnColors';

interface ProjectFormState {
  name: string;
}

function projectToFormState(project: Project): ProjectFormState {
  return {
    name: project.name,
  };
}

export function ProjectSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectIdParam = searchParams.get('projectId') ?? '';
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  // Fetch all projects
  const {
    projects,
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();

  // Selected project state
  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    searchParams.get('projectId') || ''
  );
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Form state
  const [draft, setDraft] = useState<ProjectFormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Repositories state
  const [repositories, setRepositories] = useState<Repo[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoError, setRepoError] = useState<string | null>(null);
  const [addingRepo, setAddingRepo] = useState(false);
  const [deletingRepoId, setDeletingRepoId] = useState<string | null>(null);

  // Check permissions for managing project members
  const { hasPermission } = usePermissions();
  const canManageMembers = hasPermission('project.members.manage');

  // Project members queries
  const { data: projectMembers = [], refetch: refetchMembers } = useQuery({
    queryKey: ['projectMembers', selectedProjectId],
    queryFn: () => projectMembersApi.getMembers(selectedProjectId),
    enabled: canManageMembers && !!selectedProjectId,
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: usersApi.list,
    enabled: canManageMembers,
  });

  // Mutations for project members
  const assignMemberMutation = useMutation({
    mutationFn: ({
      projectId,
      userId,
    }: {
      projectId: string;
      userId: number;
    }) => projectMembersApi.assignMember(projectId, userId),
    onSuccess: () => {
      refetchMembers();
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: ({
      projectId,
      userId,
    }: {
      projectId: string;
      userId: number;
    }) => projectMembersApi.removeMember(projectId, userId),
    onSuccess: () => {
      refetchMembers();
    },
  });

  // Columns query
  const {
    data: columns = [],
    isLoading: columnsLoading,
    refetch: refetchColumns,
  } = useProjectColumns(selectedProjectId);

  // Column mutations for reorder
  const { reorderColumns } = useColumnMutations({
    onReorderSuccess: () => {
      refetchColumns();
    },
  });

  // Handle column reorder
  const handleReorderColumn = useCallback(
    (columnId: string, direction: 'up' | 'down') => {
      const sortedColumns = [...columns].sort(
        (a, b) => a.position - b.position
      );
      const currentIndex = sortedColumns.findIndex((c) => c.id === columnId);

      if (currentIndex === -1) return;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      if (newIndex < 0 || newIndex >= sortedColumns.length) return;

      // Swap positions
      const newOrder = [...sortedColumns];
      [newOrder[currentIndex], newOrder[newIndex]] = [
        newOrder[newIndex],
        newOrder[currentIndex],
      ];

      const columnIds = newOrder.map((c) => c.id);
      reorderColumns.mutate({ projectId: selectedProjectId, columnIds });
    },
    [columns, selectedProjectId, reorderColumns]
  );

  // Check for unsaved changes (project name)
  const hasUnsavedChanges = useMemo(() => {
    if (!draft || !selectedProject) return false;
    return !isEqual(draft, projectToFormState(selectedProject));
  }, [draft, selectedProject]);

  // Handle project selection from dropdown
  const handleProjectSelect = useCallback(
    (id: string) => {
      // No-op if same project
      if (id === selectedProjectId) return;

      // Confirm if there are unsaved changes
      if (hasUnsavedChanges) {
        const confirmed = window.confirm(
          t('settings.projects.save.confirmSwitch')
        );
        if (!confirmed) return;

        // Clear local state before switching
        setDraft(null);
        setSelectedProject(null);
        setSuccess(false);
        setError(null);
      }

      // Update state and URL
      setSelectedProjectId(id);
      if (id) {
        setSearchParams({ projectId: id });
      } else {
        setSearchParams({});
      }
    },
    [hasUnsavedChanges, selectedProjectId, setSearchParams, t]
  );

  // Sync selectedProjectId when URL changes (with unsaved changes prompt)
  useEffect(() => {
    if (projectIdParam === selectedProjectId) return;

    // Confirm if there are unsaved changes
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        t('settings.projects.save.confirmSwitch')
      );
      if (!confirmed) {
        // Revert URL to previous value
        if (selectedProjectId) {
          setSearchParams({ projectId: selectedProjectId });
        } else {
          setSearchParams({});
        }
        return;
      }

      // Clear local state before switching
      setDraft(null);
      setSelectedProject(null);
      setSuccess(false);
      setError(null);
    }

    setSelectedProjectId(projectIdParam);
  }, [
    projectIdParam,
    hasUnsavedChanges,
    selectedProjectId,
    setSearchParams,
    t,
  ]);

  // Populate draft from server data
  useEffect(() => {
    if (!projects) return;

    const nextProject = selectedProjectId
      ? projects.find((p) => p.id === selectedProjectId)
      : null;

    setSelectedProject((prev) =>
      prev?.id === nextProject?.id ? prev : (nextProject ?? null)
    );

    if (!nextProject) {
      if (!hasUnsavedChanges) setDraft(null);
      return;
    }

    if (hasUnsavedChanges) return;

    setDraft(projectToFormState(nextProject));
  }, [projects, selectedProjectId, hasUnsavedChanges]);

  // Warn on tab close/navigation with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Fetch repositories when project changes
  useEffect(() => {
    if (!selectedProjectId) {
      setRepositories([]);
      return;
    }

    setLoadingRepos(true);
    setRepoError(null);
    projectsApi
      .getRepositories(selectedProjectId)
      .then(setRepositories)
      .catch((err) => {
        setRepoError(
          err instanceof Error ? err.message : 'Failed to load repositories'
        );
        setRepositories([]);
      })
      .finally(() => setLoadingRepos(false));
  }, [selectedProjectId]);

  const handleAddRepository = async () => {
    if (!selectedProjectId) return;

    const repo = await RepoPickerDialog.show({
      title: 'Select Git Repository',
      description: 'Choose a git repository to add to this project',
    });

    if (!repo) return;

    if (repositories.some((r) => r.id === repo.id)) {
      return;
    }

    setAddingRepo(true);
    setRepoError(null);
    try {
      const newRepo = await projectsApi.addRepository(selectedProjectId, {
        display_name: repo.display_name,
        git_repo_path: repo.path,
      });
      setRepositories((prev) => [...prev, newRepo]);
      queryClient.invalidateQueries({
        queryKey: ['projectRepositories', selectedProjectId],
      });
      queryClient.invalidateQueries({
        queryKey: ['repos'],
      });
      queryClient.invalidateQueries({
        queryKey: repoBranchKeys.byRepo(newRepo.id),
      });
    } catch (err) {
      setRepoError(
        err instanceof Error ? err.message : 'Failed to add repository'
      );
    } finally {
      setAddingRepo(false);
    }
  };

  const handleDeleteRepository = async (repoId: string) => {
    if (!selectedProjectId) return;

    setDeletingRepoId(repoId);
    setRepoError(null);
    try {
      await projectsApi.deleteRepository(selectedProjectId, repoId);
      setRepositories((prev) => prev.filter((r) => r.id !== repoId));
      queryClient.invalidateQueries({
        queryKey: ['projectRepositories', selectedProjectId],
      });
      queryClient.invalidateQueries({
        queryKey: ['repos'],
      });
      queryClient.invalidateQueries({
        queryKey: repoBranchKeys.byRepo(repoId),
      });
    } catch (err) {
      setRepoError(
        err instanceof Error ? err.message : 'Failed to delete repository'
      );
    } finally {
      setDeletingRepoId(null);
    }
  };

  const { updateProject } = useProjectMutations({
    onUpdateSuccess: (updatedProject: Project) => {
      // Update local state with fresh data from server
      setSelectedProject(updatedProject);
      setDraft(projectToFormState(updatedProject));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      setSaving(false);
    },
    onUpdateError: (err) => {
      setError(
        err instanceof Error ? err.message : 'Failed to save project settings'
      );
      setSaving(false);
    },
  });

  const handleSave = async () => {
    if (!draft || !selectedProject) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updateData: UpdateProject = {
        name: draft.name.trim(),
      };

      updateProject.mutate({
        projectId: selectedProject.id,
        data: updateData,
      });
    } catch (err) {
      setError(t('settings.projects.save.error'));
      console.error('Error saving project settings:', err);
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!selectedProject) return;
    setDraft(projectToFormState(selectedProject));
  };

  const updateDraft = (updates: Partial<ProjectFormState>) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return { ...prev, ...updates };
    });
  };

  if (projectsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">{t('settings.projects.loading')}</span>
      </div>
    );
  }

  if (projectsError) {
    return (
      <div className="py-8">
        <Alert variant="destructive">
          <AlertDescription>
            {projectsError instanceof Error
              ? projectsError.message
              : t('settings.projects.loadError')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="success">
          <AlertDescription className="font-medium">
            {t('settings.projects.save.success')}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.projects.title')}</CardTitle>
          <CardDescription>
            {t('settings.projects.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="project-selector">
              {t('settings.projects.selector.label')}
            </Label>
            <Select
              value={selectedProjectId}
              onValueChange={handleProjectSelect}
            >
              <SelectTrigger id="project-selector">
                <SelectValue
                  placeholder={t('settings.projects.selector.placeholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {projects && projects.length > 0 ? (
                  projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="no-projects" disabled>
                    {t('settings.projects.selector.noProjects')}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {t('settings.projects.selector.helper')}
            </p>
          </div>
        </CardContent>
      </Card>

      {selectedProject && draft && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.projects.general.title')}</CardTitle>
              <CardDescription>
                {t('settings.projects.general.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="project-name">
                  {t('settings.projects.general.name.label')}
                </Label>
                <Input
                  id="project-name"
                  type="text"
                  value={draft.name}
                  onChange={(e) => updateDraft({ name: e.target.value })}
                  placeholder={t('settings.projects.general.name.placeholder')}
                  required
                />
                <p className="text-sm text-muted-foreground">
                  {t('settings.projects.general.name.helper')}
                </p>
              </div>

              {/* Save Button */}
              <div className="flex items-center justify-between pt-4 border-t">
                {hasUnsavedChanges ? (
                  <span className="text-sm text-muted-foreground">
                    {t('settings.projects.save.unsavedChanges')}
                  </span>
                ) : (
                  <span />
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleDiscard}
                    disabled={saving || !hasUnsavedChanges}
                  >
                    {t('settings.projects.save.discard')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving || !hasUnsavedChanges}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.projects.save.saving')}
                      </>
                    ) : (
                      t('settings.projects.save.button')
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Repositories Section */}
          <Card>
            <CardHeader>
              <CardTitle>Repositories</CardTitle>
              <CardDescription>
                Manage the git repositories in this project
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {repoError && (
                <Alert variant="destructive">
                  <AlertDescription>{repoError}</AlertDescription>
                </Alert>
              )}

              {loadingRepos ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    Loading repositories...
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  {repositories.map((repo) => (
                    <div
                      key={repo.id}
                      className="flex items-center justify-between p-3 border rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() =>
                        navigate(`/settings/repos?repoId=${repo.id}`)
                      }
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{repo.display_name}</div>
                        <div className="text-sm text-muted-foreground truncate">
                          {repo.path}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRepository(repo.id);
                        }}
                        disabled={deletingRepoId === repo.id}
                        title="Delete repository"
                      >
                        {deletingRepoId === repo.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  ))}

                  {repositories.length === 0 && !loadingRepos && (
                    <div className="text-center py-4 text-sm text-muted-foreground">
                      No repositories configured
                    </div>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddRepository}
                    disabled={addingRepo}
                    className="w-full"
                  >
                    {addingRepo ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Add Repository
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Columns Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.projects.columns.title')}</CardTitle>
              <CardDescription>
                {t('settings.projects.columns.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {columnsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    {t('settings.projects.columns.loading')}
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  {columns
                    .sort((a, b) => a.position - b.position)
                    .map((column, index, sortedColumns) => {
                      const taskCountNum = Number(column.task_count);
                      const canMoveUp = index > 0;
                      const canMoveDown = index < sortedColumns.length - 1;
                      return (
                        <div
                          key={column.id}
                          className="flex items-center justify-between p-3 border rounded-md"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{
                                backgroundColor:
                                  COLUMN_COLORS_MAP[column.color] || '#6b7280',
                              }}
                            />
                            <div className="font-medium">{column.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {t('settings.projects.columns.taskCount', {
                                count: taskCountNum,
                              })}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleReorderColumn(column.id, 'up')
                              }
                              disabled={!canMoveUp || reorderColumns.isPending}
                              title="Move up"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleReorderColumn(column.id, 'down')
                              }
                              disabled={
                                !canMoveDown || reorderColumns.isPending
                              }
                              title="Move down"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                EditColumnDialog.show({
                                  column: {
                                    id: column.id,
                                    project_id: column.project_id,
                                    name: column.name,
                                    color: column.color,
                                    position: column.position,
                                    op_status_id: column.op_status_id,
                                    created_at: column.created_at,
                                    updated_at: column.updated_at,
                                  },
                                })
                              }
                              title="Edit column"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                DeleteColumnDialog.show({
                                  column: {
                                    id: column.id,
                                    project_id: column.project_id,
                                    name: column.name,
                                    color: column.color,
                                    position: column.position,
                                    op_status_id: column.op_status_id,
                                    created_at: column.created_at,
                                    updated_at: column.updated_at,
                                  },
                                  projectId: selectedProjectId,
                                  taskCount: taskCountNum,
                                }).then((result) => {
                                  if (result === 'deleted') {
                                    refetchColumns();
                                  }
                                })
                              }
                              title="Delete column"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}

                  {columns.length === 0 && !columnsLoading && (
                    <div className="text-center py-4 text-sm text-muted-foreground">
                      {t('settings.projects.columns.noColumns')}
                    </div>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      CreateColumnDialog.show({
                        projectId: selectedProjectId,
                      }).then((result) => {
                        if (result.status === 'saved') {
                          refetchColumns();
                        }
                      })
                    }
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('settings.projects.columns.addColumn')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Project Members Section */}
          {canManageMembers && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5" />
                      Project Members
                    </CardTitle>
                    <CardDescription>
                      Manage team members assigned to this project
                    </CardDescription>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {projectMembers.length} member{projectMembers.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Members */}
                <div className="space-y-2">
                  <Label>Current Members</Label>
                  {projectMembers.length === 0 ? (
                    <div className="text-center py-6 text-sm text-muted-foreground border rounded-md border-dashed">
                      <User className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      No members assigned to this project yet.
                      <br />
                      Add users below to grant them access.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {projectMembers.map((member) => {
                        const roleIcon = member.role === 'admin' ? (
                          <Crown className="h-4 w-4 text-purple-500" />
                        ) : member.role === 'developer' ? (
                          <Shield className="h-4 w-4 text-blue-600" />
                        ) : (
                          <User className="h-4 w-4 text-muted-foreground" />
                        );
                        // Can remove anyone except admin (they have implicit access)
                        const canRemove = member.role !== 'admin';
                        return (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-3 border rounded-md"
                          >
                            <div className="flex items-center gap-3">
                              {roleIcon}
                              <div>
                                <div className="font-medium">
                                  {member.username}
                                </div>
                                <div className="text-sm text-muted-foreground capitalize">
                                  {member.role}
                                </div>
                              </div>
                            </div>
                            {canRemove && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  removeMemberMutation.mutate({
                                    projectId: selectedProjectId,
                                    userId: Number(member.id),
                                  })
                                }
                                disabled={removeMemberMutation.isPending}
                                title="Remove from project"
                              >
                                {removeMemberMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Add Member */}
                <div className="space-y-2 pt-4 border-t">
                  <Label htmlFor="add-member">Add Member</Label>
                  <div className="flex gap-2">
                    <Select
                      onValueChange={(userId) =>
                        assignMemberMutation.mutate({
                          projectId: selectedProjectId,
                          userId: parseInt(userId),
                        })
                      }
                      disabled={assignMemberMutation.isPending}
                    >
                      <SelectTrigger id="add-member" className="flex-1">
                        <SelectValue placeholder="Select a user to add..." />
                      </SelectTrigger>
                      <SelectContent>
                        {allUsers
                          .filter(
                            (user) =>
                              !projectMembers.some((m) => m.id === user.id)
                          )
                          .map((user) => (
                            <SelectItem
                              key={user.id}
                              value={user.id.toString()}
                            >
                              <span className="flex items-center gap-2">
                                {user.username}
                                <span className="text-xs text-muted-foreground capitalize">
                                  ({user.role})
                                </span>
                              </span>
                            </SelectItem>
                          ))}
                        {allUsers.filter(
                          (user) =>
                            !projectMembers.some((m) => m.id === user.id)
                        ).length === 0 && (
                          <div className="p-2 text-sm text-muted-foreground text-center">
                            All users are already assigned
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Owners and Admins have implicit access to all projects.
                    Other roles must be explicitly assigned.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sticky Save Button for Project Name */}
          {hasUnsavedChanges && (
            <div className="sticky bottom-0 z-10 bg-background/80 backdrop-blur-sm border-t py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('settings.projects.save.unsavedChanges')}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleDiscard}
                    disabled={saving}
                  >
                    {t('settings.projects.save.discard')}
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {t('settings.projects.save.button')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
